import type { RefObject } from 'react'

import { useEffect, useRef } from 'react'

/**
 * Lets mouse users drag a horizontally scrolling row. Touch keeps its native
 * swipe. A drag never counts as a click on the card underneath.
 */
export const useDragScroll = <T extends HTMLElement>(): RefObject<T | null> => {
    const ref = useRef<T>(null)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        let down = false
        let moved = false
        let startX = 0
        let startLeft = 0
        let pointer = -1

        const onDown = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) return

            down = true
            moved = false
            startX = event.clientX
            startLeft = element.scrollLeft
            pointer = event.pointerId
        }

        const onMove = (event: PointerEvent) => {
            if (!down) return

            const delta = event.clientX - startX

            if (!moved && Math.abs(delta) > 6) {
                moved = true
                element.dataset.dragging = 'true'
                element.setPointerCapture(pointer)
            }

            if (moved) element.scrollLeft = startLeft - delta
        }

        const onUp = () => {
            if (!down) return

            down = false

            if (moved) {
                delete element.dataset.dragging

                if (element.hasPointerCapture(pointer)) element.releasePointerCapture(pointer)
            }
        }

        const onClick = (event: MouseEvent) => {
            if (!moved) return

            event.preventDefault()
            event.stopPropagation()
            moved = false
        }

        const onDragStart = (event: DragEvent) => event.preventDefault()

        element.addEventListener('pointerdown', onDown)
        element.addEventListener('pointermove', onMove)
        element.addEventListener('pointerup', onUp)
        element.addEventListener('pointercancel', onUp)
        element.addEventListener('click', onClick, true)
        element.addEventListener('dragstart', onDragStart)

        return () => {
            element.removeEventListener('pointerdown', onDown)
            element.removeEventListener('pointermove', onMove)
            element.removeEventListener('pointerup', onUp)
            element.removeEventListener('pointercancel', onUp)
            element.removeEventListener('click', onClick, true)
            element.removeEventListener('dragstart', onDragStart)
        }
    }, [])

    return ref
}
